import { ApiTypes } from '@app/api';

/** A report group, or `unknown` for answers that carry no gender. */
export type GenderKey = ApiTypes.GenderReportGroup | 'unknown';

/** Counts per gender. A gender the backend hid or never counted has no entry. */
export type GenderCounts = Partial<Record<GenderKey, number>>;

/** Counts the backend withholds whole when Female or Male is too small to show. */
export interface GenderBreakdown<T> {
  counts: T;
  hidden: boolean;
}

/** Offered in every account's survey, whatever its switches say. */
export const ALWAYS_OFFERED_GENDERS: ApiTypes.Gender[] = ['female', 'male', 'preferNotToSay'];

export function isGenderReportGroup(value: string): value is ApiTypes.GenderReportGroup {
  return (
    value === 'female' ||
    value === 'male' ||
    value === 'anotherGender' ||
    value === 'preferNotToSay'
  );
}

export const GENDER_LABEL_KEYS: Record<GenderKey, string> = {
  female: 'SHARED.SYSTEM.FEMALE',
  male: 'SHARED.SYSTEM.MALE',
  anotherGender: 'SHARED.SYSTEM.ANOTHER_GENDER',
  preferNotToSay: 'SHARED.SYSTEM.PREFER_NOT_TO_SAY',
  unknown: 'SHARED.SYSTEM.NO_ANSWER',
};

export const GENDER_ICONS: Record<ApiTypes.GenderReportGroup, string> = {
  male: 'man',
  female: 'woman',
  anotherGender: 'person',
  preferNotToSay: 'lock',
};

/**
 * Female and male always get a place, so a chart still shows the split it will have. The other
 * groups appear only once someone is in them: a small Another gender group arrives as `null`
 * and is counted under No answer instead.
 */
export function isShownGender(gender: GenderKey, count: number | null | undefined): boolean {
  return gender === 'female' || gender === 'male' || (count ?? 0) > 0;
}

/** Adds up per-gender counts, such as one per age group, into a total for each gender. */
export function sumGenderCounts(counts: GenderCounts[]): GenderCounts {
  return counts.reduce<GenderCounts>((totals, groupCounts) => {
    Object.entries(groupCounts).forEach(([gender, count]) => {
      totals[gender as GenderKey] = (totals[gender as GenderKey] ?? 0) + count;
    });

    return totals;
  }, {});
}

/** The venue charts name "no answer" `Unknown`; the campaign charts use an empty key. */
const BACKEND_KEYS: Record<string, GenderKey> = {
  female: 'female',
  male: 'male',
  anotherGender: 'anotherGender',
  preferNotToSay: 'preferNotToSay',
  Unknown: 'unknown',
  '': 'unknown',
};

/**
 * Reads a gender-keyed map from the backend. A `null` count is a group hidden by the minimum group
 * size, so it is left out rather than drawn as nobody.
 */
export function toGenderCounts(distribution: Record<string, number | null>): GenderCounts {
  return Object.entries(distribution).reduce<GenderCounts>((counts, [key, count]) => {
    const gender = BACKEND_KEYS[key];

    if (gender && count !== null) {
      counts[gender] = (counts[gender] ?? 0) + count;
    }

    return counts;
  }, {});
}
